import { Box, Grid, IconButton, Stack, TextField, Typography } from '@mui/material'
import { HoldTypeResponseDto } from '@planning/app/api'
import { HoldTypeAutocomplete } from '@planning/components/autocompletes/hold-type-autocomplete/HoldTypeAutocomplete'
import { useTranslate } from '@tolgee/react'
import { BinIcon, ColoredLabel, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { HoldEntryStore } from '../organisms/stores/OrderHoldsSectionStore'

interface HoldEntryCardProps {
  holdStore: HoldEntryStore
  index: number
  onRemove: () => void
  canRemove?: boolean
}
export const HoldEntryCard = observer(
  ({ holdStore, index, onRemove, canRemove = true }: HoldEntryCardProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const [selectedType, setSelectedType] = useState<HoldTypeResponseDto | null>(null)

    const restrictions = selectedType?.restrictions ?? []

    return (
      <Stack gap={theme.customSpacing.m}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='subtitle2' color='text.primary'>
            {t('holdNumber', 'Hold {number}', { number: index + 1 })}
          </Typography>
          {canRemove && (
            <IconButton size='small' onClick={onRemove}>
              <BinIcon />
            </IconButton>
          )}
        </Box>

        <Grid container spacing={theme.customSpacing.m}>
          <Grid size={12}>
            <HoldTypeAutocomplete
              value={holdStore.holdTypeId.value}
              error={!!holdStore.holdTypeId.error}
              required
              disabled={holdStore.backendId !== undefined}
              onBlur={holdStore.holdTypeId.touch}
              onChange={newValue => {
                holdStore.holdTypeId.setValue(newValue?.id ?? null)
                holdStore.holdTypeName.setValue(newValue?.name ?? '')
                setSelectedType(newValue)
              }}
            />
          </Grid>

          {restrictions.length > 0 && (
            <Grid size={12}>
              <Stack direction='row' flexWrap='wrap' gap={theme.customSpacing.xs}>
                {restrictions.map(r => {
                  const reactionLabel = r.reaction === 'Warning' ? 'Warning' : 'Block'
                  const typeLabel = r.restrictionType.toLowerCase()
                  const labelKey = `${reactionLabel.toLowerCase()}${r.restrictionType}`
                  const labelValue = `${reactionLabel} ${typeLabel}`

                  return (
                    <ColoredLabel
                      key={r.restrictionType}
                      color='secondary'
                      label={t(labelKey, labelValue)}
                    />
                  )
                })}
              </Stack>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              value={holdStore.description.value}
              onChange={holdStore.description.onChange}
              label={t('description', 'Description')}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </Stack>
    )
  },
)
